import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { MediaAsset } from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../../i18n';
import { api } from '../../../lib/api';
import { defaultPlaylistDurationSeconds } from '../../content/buildContentLibrary';

function isReady(asset: MediaAsset): boolean {
  return asset.status === 'ready';
}

type UsePlayContentOnScreenArgs = {
  organisationId: string;
  screenId: string;
  /** When false, skip the library fetch (picker closed). */
  enabled?: boolean;
  onPlayed: (playlistName: string) => void;
};

/**
 * Shared load + one-tap play for putting a library item on a display.
 */
export function usePlayContentOnScreen({
  organisationId,
  screenId,
  enabled = true,
  onPlayed,
}: UsePlayContentOnScreenArgs) {
  const { t } = useTranslation('displays');
  const [playingAssetId, setPlayingAssetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaQuery = useQuery({
    queryKey: ['media-assets', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
    enabled,
  });

  const readyAssets = useMemo(
    () => (mediaQuery.data ?? []).filter(isReady),
    [mediaQuery.data],
  );

  const play = useMutation({
    mutationFn: (asset: MediaAsset) =>
      api.playContentOnScreen(organisationId, screenId, {
        mediaAssetId: asset.id,
        durationSeconds: defaultPlaylistDurationSeconds(asset),
      }),
    onSuccess: (result) => onPlayed(result.playlistName),
    onError: (err) => {
      setPlayingAssetId(null);
      setError(apiErrorMessage(err, t('playContent.playError')));
    },
  });

  const selectAsset = (assetId: string) => {
    const asset = readyAssets.find((item) => item.id === assetId);
    if (!asset || play.isPending) return;
    setError(null);
    setPlayingAssetId(assetId);
    play.mutate(asset);
  };

  return {
    readyAssets,
    mediaPending: mediaQuery.isPending,
    mediaError: mediaQuery.isError,
    playingAssetId,
    playPending: play.isPending,
    error,
    selectAsset,
  };
}
